import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppError, AuthSelector, CORE_ROUTE, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { DeviceConfigService, DeviceType } from "@obg/ngx.global";
import { CookieService, PlatformService } from "@obg/ngx.global.evo";
import { Route, RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { asyncScheduler, merge, of } from "rxjs";
import { catchError, filter, map, observeOn, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { CommonState } from "../state";

import { CAMPAIGN_ACTION_TYPE, CampaignAction } from "./campaign.action";
import { CampaignConfigService } from "./campaign.config";
import { CAMPAIGN_MESSAGE_CODES, CAMPAIGN_MESSAGE_CONTEXT } from "./campaign.const";
import { Campaign } from "./campaign.model";
import { CampaignService } from "./campaign.service";

@Injectable()
export class CampaignEffect {
	enrollCampaign$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(RouteAction.resolveEnd),
				take(1),
				observeOn(asyncScheduler),
				switchMap(() =>
					this.store.pipe(
						AuthSelector.isAuthenticated$,
						withLatestFrom(this.store.select(RouteSelector.getCurrent)),
						filter(([isAuthenticated]) => isAuthenticated),
						take(1),
						map(([, route]) => this.campaign(route)),
						ignoreNil(),
						switchMap((campaign) =>
							this.service.enrollToCampaign(campaign).pipe(
								map(() =>
									campaign.isJoinWithCode
										? this.action.joinByCodeSuccess()
										: this.action.optInSuccess()
								),
								catchError((error: AppError) =>
									campaign.isJoinWithCode
										? of(this.action.joinByCodeFail(error))
										: of(this.action.optInFail(error))
								),
								tap(() =>
									this.cookieService.delete(
										campaign.isJoinWithCode ? this.campaignCodeParamName : this.optInParamName
									)
								)
							)
						)
					)
				)
			)
		)
	);

	redirectToLogin$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(RouteAction.resolveEnd),
				observeOn(asyncScheduler),
				map(({ payload: { current } }) => ({
					route: current,
					campaign: this.campaign(current),
					platform: this.deviceConfigService.getPlatform(),
				})),
				take(1),
				filter((data) => !!data.campaign),
				switchMap((data) =>
					this.store.pipe(
						AuthSelector.isAuthenticated$,
						take(1),
						filter((isAuthenticated) => !isAuthenticated),
						tap(
							() =>
								data.campaign!.source === "url" &&
								this.cookieService.set({
									name: data.campaign?.isJoinWithCode
										? this.campaignCodeParamName
										: this.optInParamName,
									value: data.campaign!.campaignCode,
								})
						),
						filter(
							() =>
								!!data.platform &&
								data.route.name !== CORE_ROUTE.registration.register &&
								data.campaign!.source !== "cookie"
						),
						map(() => {
							if (data.platform === DeviceType.Desktop) {
								const pattern = new RegExp("returnUrl=\\/([^?]+)");
								const externalUrl = decodeURIComponent(data.route.externalUrl);
								const queryParams = externalUrl.match(pattern);
								if (queryParams && queryParams.length > 1) {
									return data.route.externalUrl.includes("returnUrl=")
										? RouteAction.setQueryParams({ returnUrl: queryParams[1] })
										: RouteAction.setQueryParams({
												action: CORE_ROUTE.auth.login,
												returnUrl: queryParams[1],
											});
								}
							}
							return RouteAction.navigate(CORE_ROUTE.auth.login);
						})
					)
				)
			)
		)
	);

	initMessages$ = this.platformService.forClient(() =>
		createEffect(() =>
			merge(
				this.messageService.mapErrorByContext$({
					[CAMPAIGN_ACTION_TYPE.joinByCodeFail]: CAMPAIGN_MESSAGE_CONTEXT.joinCampaignError,
					[CAMPAIGN_ACTION_TYPE.optInFail]: CAMPAIGN_MESSAGE_CONTEXT.optInCampaignError,
				}),
				this.messageService.map$({
					[CAMPAIGN_ACTION_TYPE.joinByCodeSuccess]: {
						context: CAMPAIGN_MESSAGE_CONTEXT.joinCampaignSuccess,
						scope: MessageScopeType.Local,
						type: MessageType.Success,
						messageCode: CAMPAIGN_MESSAGE_CODES.joinCampaign.success,
					},
					[CAMPAIGN_ACTION_TYPE.optInSuccess]: {
						context: CAMPAIGN_MESSAGE_CONTEXT.optInCampaignSuccess,
						scope: MessageScopeType.Local,
						type: MessageType.Success,
						messageCode: CAMPAIGN_MESSAGE_CODES.optInCampaign.success,
					},
				})
			)
		)
	);

	private campaignCodeParamName: string;
	private optInParamName: string;

	constructor(
		private actions$: Actions,
		private action: CampaignAction,
		private service: CampaignService,
		private store: Store<CommonState>,
		private config: CampaignConfigService,
		private messageService: MessageService,
		private cookieService: CookieService,
		private platformService: PlatformService,
		private deviceConfigService: DeviceConfigService
	) {
		this.campaignCodeParamName = this.config.getCampaignCodeParamName();
		this.optInParamName = this.config.getOptInParamName();
	}

	private campaign(route: Route | undefined): Campaign | undefined {
		let campaignCode = route?.queryParams && route.queryParams[this.optInParamName];
		if (campaignCode) {
			return { campaignCode, source: "url", isJoinWithCode: false };
		}

		campaignCode = route?.queryParams && route.queryParams[this.campaignCodeParamName];
		if (campaignCode) {
			return { campaignCode, source: "url", isJoinWithCode: true };
		}

		const returnUrl = decodeURIComponent(route?.queryParams?.returnUrl);
		const hasCampaignCode = returnUrl?.includes(this.campaignCodeParamName);
		const hasOptInParamName = returnUrl?.includes(this.optInParamName);

		if (hasCampaignCode || hasOptInParamName) {
			const paramName = hasCampaignCode ? this.campaignCodeParamName : this.optInParamName;
			const regex = new RegExp(`[\\?&]${paramName}=([^&#]+)`);
			const code = returnUrl?.match(regex);
			if (code && code?.length > 0) {
				campaignCode = code[1];
				return { campaignCode, source: "url", isJoinWithCode: hasCampaignCode };
			}
		}

		campaignCode = this.cookieService.get(this.optInParamName);
		if (campaignCode) {
			return { campaignCode, source: "cookie", isJoinWithCode: false };
		}

		campaignCode = this.cookieService.get(this.campaignCodeParamName);
		if (campaignCode) {
			return { campaignCode, source: "cookie", isJoinWithCode: true };
		}

		return undefined;
	}
}
